import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, GraduationCap, Route, FileQuestion, Download } from "lucide-react";
import { api, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";

/* ===================== DIAGNOSTIC FORM ===================== */
const _uid = () => Math.random().toString(36).slice(2, 10);
const _slug = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 40).replace(/^-|-$/g, "");

function _buildInit(initial) {
  const types = (initial?.types?.length ? initial.types : [{ label: "" }, { label: "" }, { label: "" }]).map((t) => ({
    _id: _uid(), key: t.key || "", label: t.label || "", description: t.description || "", scripture: t.scripture || "", growth: t.growth || "",
  }));
  const keyToId = {}; types.forEach((t) => { if (t.key) keyToId[t.key] = t._id; });
  const questions = (initial?.questions?.length ? initial.questions : [{ q: "", options: [{ text: "", type_key: "" }, { text: "", type_key: "" }] }]).map((q) => ({
    q: q.q || "",
    options: (q.options || []).map((o) => ({ text: o.text || "", type_id: keyToId[o.type_key] || "" })),
  }));
  return { types, questions };
}

function DiagnosticForm({ initial, drops, onSaved, onCancel }) {
  const [init] = useState(() => _buildInit(initial));
  const [dropId, setDropId] = useState(initial?.drop_id || "");
  const [title, setTitle] = useState(initial?.title || "");
  const [intro, setIntro] = useState(initial?.intro || "");
  const [types, setTypes] = useState(init.types);
  const [questions, setQuestions] = useState(init.questions);

  // ---- types ----
  const setType = (i, patch) => setTypes((ts) => ts.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const addType = () => setTypes((ts) => [...ts, { _id: _uid(), key: "", label: "", description: "", scripture: "", growth: "" }]);
  const removeType = (i) => {
    const removedId = types[i]._id;
    setTypes((ts) => ts.filter((_, idx) => idx !== i));
    // clear any option pointing at the removed type
    setQuestions((qs) => qs.map((q) => ({ ...q, options: q.options.map((o) => (o.type_id === removedId ? { ...o, type_id: "" } : o)) })));
  };

  // ---- questions ----
  const setQ = (i, patch) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const addQuestion = () => setQuestions((qs) => [...qs, { q: "", options: [{ text: "", type_id: "" }, { text: "", type_id: "" }] }]);
  const removeQuestion = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));
  const addOption = (i) => setQ(i, { options: [...questions[i].options, { text: "", type_id: "" }] });
  const setOption = (i, oi, patch) => { const opts = questions[i].options.map((o, idx) => (idx === oi ? { ...o, ...patch } : o)); setQ(i, { options: opts }); };
  const removeOption = (i, oi) => setQ(i, { options: questions[i].options.filter((_, idx) => idx !== oi) });

  const save = async () => {
    if (!dropId) { toast.error("Pick the drop this diagnostic belongs to"); return; }
    if (!title.trim()) { toast.error("Give the diagnostic a title"); return; }
    if (types.length < 2) { toast.error("Define at least 2 types — the lenses of the heart"); return; }
    for (const t of types) {
      if (!t.label.trim()) { toast.error("Every type needs a label"); return; }
      if (!t.description.trim()) { toast.error(`Type "${t.label || "?"}" needs a description (what it reveals)`); return; }
    }
    // generate stable keys (slug + dedupe), map client id -> key
    const used = {}; const typesOut = types.map((t) => {
      let k = (t.key && t.key.trim()) || _slug(t.label) || ("type-" + _uid().slice(0, 4));
      let base = k, n = 1; while (used[k]) { n += 1; k = `${base}-${n}`; } used[k] = true;
      return { ...t, key: k };
    });
    const idToKey = {}; typesOut.forEach((t) => { idToKey[t._id] = t.key; });

    const questionsOut = [];
    for (const [qi, q] of questions.entries()) {
      if (!q.q.trim()) { toast.error(`Question ${qi + 1} needs text`); return; }
      const opts = q.options.filter((o) => o.text.trim());
      if (opts.length < 2) { toast.error(`Question ${qi + 1} needs at least 2 options`); return; }
      for (const o of opts) {
        if (!o.type_id || !idToKey[o.type_id]) { toast.error(`Every option in question ${qi + 1} must point to a type`); return; }
      }
      questionsOut.push({ q: q.q.trim(), options: opts.map((o) => ({ text: o.text.trim(), type_key: idToKey[o.type_id] })) });
    }

    const payload = {
      drop_id: dropId,
      title: title.trim(),
      intro: intro.trim(),
      types: typesOut.map(({ _id, ...rest }) => rest),
      questions: questionsOut,
    };
    try {
      if (initial?.id) await api.patch(`/admin/quizzes/${initial.id}`, payload);
      else await api.post("/admin/quizzes", payload);
      toast.success(initial?.id ? "Diagnostic updated" : "Diagnostic created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="panel p-6 space-y-5 border-gold" data-testid="quiz-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="overline">// ATTACH TO DROP</label>
          <select data-testid="quiz-drop" value={dropId} onChange={(e) => setDropId(e.target.value)}>
            <option value="">— Select a drop —</option>
            {drops.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </div>
        <div>
          <label className="overline">// DIAGNOSTIC TITLE</label>
          <input data-testid="quiz-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. What's your relationship with Mammon?" />
        </div>
      </div>
      <div>
        <label className="overline">// INTRO (sets the frame — there are no wrong answers)</label>
        <textarea rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="Answer honestly. This isn't a test — it's a mirror. Your result reveals how you currently relate to money and time, and the next step toward freedom." />
      </div>

      {/* TYPES */}
      <div className="space-y-3">
        <div className="overline">// THE TYPES — the lenses each result reveals (no type is 'wrong')</div>
        {types.map((t, i) => (
          <div key={t._id} className="panel p-4 space-y-2" data-testid={`type-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <input value={t.label} onChange={(e) => setType(i, { label: e.target.value })} placeholder={`Type ${i + 1} label — e.g. "The Hoarder", "The Guardian"`} className="flex-1" data-testid={`type-label-${i}`} />
              {types.length > 2 && <button type="button" onClick={() => removeType(i)} className="text-ruby hover:text-red-400 mt-2"><Trash2 className="w-4 h-4" /></button>}
            </div>
            <textarea rows={2} value={t.description} onChange={(e) => setType(i, { description: e.target.value })} placeholder="What this type reveals about you (shown as the result)." />
            <div className="grid md:grid-cols-2 gap-2">
              <input value={t.scripture} onChange={(e) => setType(i, { scripture: e.target.value })} placeholder="Scripture (optional)" />
              <input value={t.growth} onChange={(e) => setType(i, { growth: e.target.value })} placeholder="One growth step for this type (optional)" />
            </div>
          </div>
        ))}
        <button type="button" onClick={addType} className="btn-ghost text-xs" data-testid="add-type"><Plus className="w-3 h-3" />Add type</button>
      </div>

      {/* QUESTIONS */}
      <div className="space-y-4">
        <div className="overline">// QUESTIONS — each option points to a type (no correct answer)</div>
        {questions.map((q, i) => (
          <div key={i} className="panel p-4 space-y-3" data-testid={`quiz-q-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <label className="overline">// QUESTION {i + 1}</label>
                <textarea rows={2} value={q.q} onChange={(e) => setQ(i, { q: e.target.value })} placeholder="Describe a real situation. Ask what they'd actually do or feel." />
              </div>
              {questions.length > 1 && <button type="button" onClick={() => removeQuestion(i)} className="text-ruby hover:text-red-400 mt-6" data-testid={`quiz-remove-q-${i}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
            <div className="space-y-2">
              <label className="overline">// OPTIONS — assign each to a type</label>
              {q.options.map((o, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input value={o.text} onChange={(e) => setOption(i, oi, { text: e.target.value })} placeholder="An honest answer someone might give" className="flex-1" data-testid={`opt-text-${i}-${oi}`} />
                  <select value={o.type_id} onChange={(e) => setOption(i, oi, { type_id: e.target.value })} className="!w-auto" data-testid={`opt-type-${i}-${oi}`}>
                    <option value="">→ type…</option>
                    {types.map((t) => <option key={t._id} value={t._id}>{t.label || "(unnamed type)"}</option>)}
                  </select>
                  {q.options.length > 2 && <button type="button" onClick={() => removeOption(i, oi)} className="text-textMuted hover:text-ruby">×</button>}
                </div>
              ))}
              <button type="button" onClick={() => addOption(i)} className="btn-ghost text-xs"><Plus className="w-3 h-3" />Add option</button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addQuestion} className="btn-ghost text-sm" data-testid="quiz-add-question"><Plus className="w-4 h-4" />Add question</button>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={save} className="btn-gold" data-testid="quiz-save">{initial?.id ? "Update diagnostic" : "Create diagnostic"}</button>
        {onCancel && <button onClick={onCancel} className="btn-ghost">Cancel</button>}
      </div>
    </div>
  );
}

/* ===================== DIAGNOSTIC MANAGER ===================== */
function DiagnosticManager({ drops }) {
  const [quizzes, setQuizzes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/admin/quizzes").then((r) => setQuizzes(r.data.quizzes)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Delete this diagnostic?")) return; await api.delete(`/admin/quizzes/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-4">
      <div className="panel p-5 border-borderGoldHi" data-testid="diagnostic-rules">
        <div className="overline mb-2">// HOW TO BUILD A DIAGNOSTIC THAT FREES (NOT BOXES)</div>
        <ul className="text-textMuted text-sm space-y-1.5">
          <li>• <b className="text-cream">Three types or more.</b> Two-type, black-and-white diagnostics box people in. People move through seasons, cycles, warfare and rest — give the mirror enough faces to be true.</li>
          <li>• <b className="text-cream">Write each result as "right now," not "forever."</b> A type names where someone is this season and one next step — and should feel like it can change as God forms them.</li>
          <li>• <b className="text-cream">No wrong answers.</b> Every option honestly belongs to a type. Never plant an obviously "godly" choice, or people perform instead of confess.</li>
          <li>• <b className="text-cream">As many questions as you need.</b> 5–8 is a strong range: enough to be stable, few enough to finish.</li>
          <li>• Read <b className="text-cream">The Discipleship Codex</b> (button at the top) for the full Kingdom principles behind how this works.</li>
        </ul>
      </div>
      <div className="flex items-center justify-between">
        <div className="overline">// {quizzes.length} diagnostics · mirrors of the heart, never tests of shame</div>
        {!creating && !editing && <button data-testid="new-quiz" onClick={() => setCreating(true)} className="btn-gold"><Plus className="w-4 h-4" /> New diagnostic</button>}
      </div>
      {creating && <DiagnosticForm drops={drops} onSaved={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}
      {editing && <DiagnosticForm drops={drops} initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      <div className="space-y-2">
        {quizzes.map((q) => (
          <div key={q.id} className="panel p-4 flex items-start justify-between gap-4" data-testid={`quiz-row-${q.id}`}>
            <div>
              <div className="overline mb-1">{q.questions?.length || 0} questions · {q.types?.length || 0} types · ON: {q.drop_title || "(drop removed)"}</div>
              <h4 className="font-display text-xl text-cream">{q.title}</h4>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(q)} className="btn-ghost text-xs !py-2 !px-3">Edit</button>
              <button onClick={() => del(q.id)} className="btn-ghost text-xs !py-2 !px-3 !border-ruby !text-ruby">Delete</button>
            </div>
          </div>
        ))}
        {quizzes.length === 0 && !creating && <p className="text-textMuted">No diagnostics yet. Build the first mirror and attach it to a drop.</p>}
      </div>
    </div>
  );
}

/* ===================== PATH FORM ===================== */
function PathForm({ initial, drops, onSaved, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [ordered, setOrdered] = useState(initial?.ordered_drop_ids || []);
  const [pick, setPick] = useState("");

  const dropTitle = (id) => drops.find((d) => d.id === id)?.title || "(drop removed)";
  const addDrop = () => { if (pick && !ordered.includes(pick)) { setOrdered([...ordered, pick]); setPick(""); } };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= ordered.length) return;
    const arr = [...ordered]; [arr[i], arr[j]] = [arr[j], arr[i]]; setOrdered(arr);
  };
  const remove = (i) => setOrdered(ordered.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!title.trim()) { toast.error("Name the path (its one summit)"); return; }
    if (ordered.length === 0) { toast.error("Add at least one drop to the path"); return; }
    const payload = { title: title.trim(), description: description.trim(), ordered_drop_ids: ordered };
    try {
      if (initial?.id) await api.patch(`/admin/learning-paths/${initial.id}`, payload);
      else await api.post("/admin/learning-paths", payload);
      toast.success(initial?.id ? "Path updated" : "Path created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const available = drops.filter((d) => !ordered.includes(d.id));

  return (
    <div className="panel p-6 space-y-4 border-gold" data-testid="path-form">
      <div><label className="overline">// PATH TITLE (the one summit)</label><input data-testid="path-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. From Fear of Lack to Ruling Your Money" /></div>
      <div><label className="overline">// DESCRIPTION (name the destination)</label><textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="By the end of this path, a steward can ______." /></div>

      <div>
        <label className="overline">// THE JOURNEY — order matters (foundations first, a doing last)</label>
        <div className="space-y-2 mt-2">
          {ordered.map((id, i) => (
            <div key={id} className="flex items-center gap-3 panel p-3" data-testid={`path-stage-${i}`}>
              <span className="font-display text-2xl text-gold w-8">{i + 1}</span>
              <div className="flex-1 text-cream">{dropTitle(id)}</div>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-textMuted hover:text-gold disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === ordered.length - 1} className="text-textMuted hover:text-gold disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => remove(i)} className="text-ruby hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {ordered.length === 0 && <p className="text-textMuted text-sm">No stages yet. Add drops below.</p>}
        </div>
        <div className="flex gap-2 mt-3">
          <select data-testid="path-add-select" value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1">
            <option value="">— Add a drop to the journey —</option>
            {available.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
          <button type="button" onClick={addDrop} className="btn-ghost" data-testid="path-add-drop"><Plus className="w-4 h-4" />Add</button>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={save} className="btn-gold" data-testid="path-save">{initial?.id ? "Update path" : "Create path"}</button>
        {onCancel && <button onClick={onCancel} className="btn-ghost">Cancel</button>}
      </div>
    </div>
  );
}

/* ===================== PATH MANAGER ===================== */
function PathManager({ drops }) {
  const [paths, setPaths] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/admin/learning-paths").then((r) => setPaths(r.data.paths)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Delete this path? Member progress for it is also cleared.")) return; await api.delete(`/admin/learning-paths/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="overline">// {paths.length} learning paths · discipleship journeys</div>
        {!creating && !editing && <button data-testid="new-path" onClick={() => setCreating(true)} className="btn-gold"><Plus className="w-4 h-4" /> New path</button>}
      </div>
      {creating && <PathForm drops={drops} onSaved={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}
      {editing && <PathForm drops={drops} initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      <div className="space-y-2">
        {paths.map((p) => (
          <div key={p.id} className="panel p-4 flex items-start justify-between gap-4" data-testid={`path-row-${p.id}`}>
            <div>
              <div className="overline mb-1">{p.ordered_drop_ids?.length || 0} stages</div>
              <h4 className="font-display text-xl text-cream">{p.title}</h4>
              {p.description && <p className="text-textMuted text-sm mt-1">{p.description}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(p)} className="btn-ghost text-xs !py-2 !px-3">Edit</button>
              <button onClick={() => del(p.id)} className="btn-ghost text-xs !py-2 !px-3 !border-ruby !text-ruby">Delete</button>
            </div>
          </div>
        ))}
        {paths.length === 0 && !creating && <p className="text-textMuted">No paths yet. Arrange your drops into the first journey.</p>}
      </div>
    </div>
  );
}

/* ===================== DISCIPLESHIP TAB ===================== */
export function DiscipleshipTab() {
  const [view, setView] = useState("quizzes");
  const [drops, setDrops] = useState([]);
  useEffect(() => { api.get("/admin/drops").then((r) => setDrops(r.data.drops || [])); }, []);
  return (
    <div className="space-y-6" data-testid="discipleship-tab">
      <div className="panel p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <div className="overline mb-2">// DISCIPLESHIP · DIAGNOSTICS & LEARNING PATHS</div>
            <h2 className="font-display text-3xl text-cream">Form them, don't just entertain them.</h2>
            <p className="text-textMuted text-sm mt-2">Every mechanic is here — you bring the content. Attach <em>diagnostics</em> to your drops where members discover their type (no right or wrong, just a mirror and a next step), and arrange your drops into <em>discipleship journeys</em> that climb foundations → application. The Codex below teaches the full mindset.</p>
          </div>
          <a data-testid="discipleship-codex-link" href={`${BACKEND_URL}/api/admin/downloads/discipleship-codex`} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex shrink-0"><Download className="w-3 h-3" /> The Discipleship Codex (PDF)</a>
        </div>
      </div>

      <div className="flex gap-3">
        <button data-testid="disc-view-quizzes" onClick={() => setView("quizzes")} className={view === "quizzes" ? "btn-gold" : "btn-ghost"}><FileQuestion className="w-4 h-4" /> Diagnostics</button>
        <button data-testid="disc-view-paths" onClick={() => setView("paths")} className={view === "paths" ? "btn-gold" : "btn-ghost"}><Route className="w-4 h-4" /> Learning Paths</button>
      </div>

      {view === "quizzes" ? <DiagnosticManager drops={drops} /> : <PathManager drops={drops} />}
    </div>
  );
}
